// Normalize message for comparison (treat null, undefined, and empty string as equivalent)
const normalizeMessage = (message) => {
  if (!message || (typeof message === 'string' && message.trim() === '')) {
    return '';
  }
  if (typeof message === 'string') {
    return message.trim();
  }
  return String(message).trim();
};

const messagesAreSame = (firstMessage, secondMessage) => (
  normalizeMessage(firstMessage) === normalizeMessage(secondMessage)
);

export const mergeItems = ({ items = [], item }) => {
  if (items.length === 0) {
    return [item];
  }

  const sameItemIndex = items.findIndex(
    (i) => i.id === item.id &&
      i.selection.size.id === item.selection.size.id &&
      modifiersAreSame(i.selection.modifiers, item.selection.modifiers) &&
      messagesAreSame(i.selection.message, item.selection.message),
  );

  if (sameItemIndex === -1) {
    return [...items, item];
  }

  const mergedItem = items[sameItemIndex];

  return [
    ...items.filter((_, index) => index !== sameItemIndex),
    {
      ...mergedItem,
      qty: mergedItem.selection.qty + item.selection.qty,

      selection: {
        ...mergedItem.selection,
        qty: mergedItem.selection.qty + item.selection.qty,
      },
    },
  ];
};

export function sortItems(items) {
  return items.sort((a, b) => a.id - b.id);
}

function modifiersAreSame(firstModifiers, secondModifiers) {
  const firstModifiersValue = Object.values(firstModifiers);
  const secondModifiersValue = Object.values(secondModifiers);
  if (firstModifiersValue.length !== secondModifiersValue.length) {
    return false;
  }
  return firstModifiersValue.every((fm) => secondModifiersValue.some(
    (sm) => sm.id === fm.id && sm.qty === fm.qty,
  ));
}

// ─── Order-item consolidation (for the cart preview) ───────────────────────

const normalizeOrderMessage = (message) => {
  if (!message || (typeof message === 'string' && message.trim() === '')) return '';
  return String(message).trim();
};

function orderOptionsAreSame(firstOptions = [], secondOptions = []) {
  if (firstOptions.length !== secondOptions.length) return false;
  return firstOptions.every((fo) => secondOptions.some(
    (so) => so.modifier_option?.id === fo.modifier_option?.id && so.qty === fo.qty,
  ));
}

function getSizePosition(size) {
  if (size == null) return null;
  if (typeof size === 'object') return size.position ?? null;
  return size;
}

/**
 * Returns true when two order-item records are considered identical
 * (same item, same size, same modifiers, same special note).
 */
export function orderItemsAreIdentical(a, b) {
  if (a.item_related?.id !== b.item_related?.id) return false;
  if (getSizePosition(a.size) !== getSizePosition(b.size)) return false;
  if (normalizeOrderMessage(a.message) !== normalizeOrderMessage(b.message)) return false;
  if (!orderOptionsAreSame(a.order_options, b.order_options)) return false;
  return true;
}

/**
 * Takes a list of existing order-items and a new/updated item.
 *
 * • On **add** (newOrderItem.isNew === true):
 *   - If an identical item already exists → increment its qty, discard the new one.
 *   - Otherwise → append the new item.
 *
 * • On **edit** (newOrderItem.isNew !== true):
 *   - Replace the edited entry with the new values first.
 *   - Then scan for a *different* item that is now identical to the edited one.
 *   - If found → merge quantities into that other item and remove the edited one.
 *   - Otherwise → keep the updated item as-is.
 */
export function consolidateOrderItems(existingItems, newOrderItem) {
  const isNew = Boolean(newOrderItem.isNew);

  // Strip isNew so it never lingers in stored cart state
  const cleanItem = { ...newOrderItem, isNew: false };

  if (isNew) {
    const matchIndex = existingItems.findIndex(
      (i) => orderItemsAreIdentical(i, cleanItem),
    );
    if (matchIndex !== -1) {
      return existingItems.map((i, idx) => (
        idx === matchIndex ? { ...i, qty: i.qty + (cleanItem.qty || 1) } : i
      ));
    }
    return [...existingItems, cleanItem];
  }

  // Edit path: first apply the update, then look for a duplicate
  const updatedList = existingItems.map((i) => (i.id === cleanItem.id ? cleanItem : i));

  const duplicateIndex = updatedList.findIndex(
    (i) => i.id !== cleanItem.id && orderItemsAreIdentical(i, cleanItem),
  );

  if (duplicateIndex !== -1) {
    return updatedList
      .map((i, idx) => (
        idx === duplicateIndex ? { ...i, qty: i.qty + cleanItem.qty } : i
      ))
      .filter((i) => i.id !== cleanItem.id);
  }

  return updatedList;
}

export const pluralize = (word, qty) => {
  if (qty < 2) {
    return word;
  }
  if (word.toLowerCase() === 'person') {
    return 'people';
  }
  if (word.endsWith('y') && !word.endsWith('ay') && !word.endsWith('ey') && !word.endsWith('iy') && !word.endsWith('oy') && !word.endsWith('uy')) {
    return `${word.slice(0, -1)}ies`;
  }
  let needES = false;
  ['s', 'ss', 'sh', 'ch', 'x', 'z'].forEach((char) => {
    if (word.endsWith(char)) needES = true;
  });
  return `${word}${needES ? 'es' : 's'}`;
};
